import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

// Free recording slug - shown with a FREE badge
const FREE_RECORDING_SLUG = 'freedom-from-insomnia';

export const ProductCard = ({ recording, index = 0 }) => {
    const { addToCart, isInCart } = useCart();
    const inCart = isInCart(recording.id);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    
    const isFreeRecording = recording.slug === FREE_RECORDING_SLUG;

    const handleFreeDownload = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/signup`, {});
            const link = document.createElement('a');
            link.href = res.data.download_url;
            link.download = 'Freedom from Insomnia.mp3';
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Your free recording is downloading. Enjoy!');
        } catch (err) {
            toast.error('Download failed. Please try again.');
        }
    };

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inCart) {
            addToCart(recording);
        }
    };
    
    return (
        <Link 
            to={`/recording/${recording.slug}`}
            className={`product-card block bg-white rounded-2xl overflow-hidden shadow-soft opacity-0 animate-fade-in-up`}
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`product-card-${recording.id}`}
        >
            <div className="aspect-square relative overflow-hidden bg-nature-100">
                {!imageError && (
                    <img 
                        src={recording.cover_image} 
                        alt={recording.title}
                        className={`w-full h-full object-cover transition-all duration-500 hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                        referrerPolicy="no-referrer"
                    />
                )}
                {imageError && (
                    <div className="w-full h-full flex items-center justify-center bg-nature-100">
                        <span className="text-nature-800/40 font-heading text-sm text-center px-4">
                            {recording.title}
                        </span>
                    </div>
                )}
            </div>
            
            <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-nature-900 mb-2 line-clamp-2">
                    {recording.title}
                </h3>
                
                <div className="flex items-center gap-2 text-nature-800/60 mb-3">
                    <span className="text-sm font-body">{recording.category}</span>
                </div>
                
                <div className="flex items-center justify-between">
                    {isFreeRecording ? (
                        <>
                            <span className="text-xl font-heading font-semibold text-green-600">
                                FREE
                            </span>
                            <Button
                                onClick={handleFreeDownload}
                                size="sm"
                                className="rounded-full bg-green-600 hover:bg-green-700"
                                data-testid={`free-download-${recording.id}`}
                            >
                                <Gift className="w-4 h-4 mr-1" />
                                Free Download
                            </Button>
                        </>
                    ) : (
                        <>
                            <span className="text-xl font-heading font-semibold text-copper-500">
                                €{recording.price.toFixed(2)}
                            </span>
                            <Button
                                onClick={handleAddToCart}
                                size="sm"
                                className={`rounded-full transition-all ${
                                    inCart 
                                        ? 'bg-nature-800 hover:bg-nature-800' 
                                        : 'bg-copper-500 hover:bg-copper-600'
                                }`}
                                data-testid={`add-to-cart-${recording.id}`}
                            >
                                {inCart ? (
                                    <>
                                        <Check className="w-4 h-4 mr-1" />
                                        Added
                                    </>
                                ) : (
                                    <>
                                        <ShoppingCart className="w-4 h-4 mr-1" />
                                        Add
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
};
